import { useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import ConsultationModal from "@/components/home/ConsultationModal";
import BtnArrow from "@/components/ui/btn-arrow";
import SwipeHint from "@/components/ui/SwipeHint";
import { useHeroSlides } from "@/hooks/useHeroSlides";
import { HERO, DIVERSITY } from "@/lib/heroAlgorithm";
import { noteShown, recordSignal } from "@/lib/visitorTaste";
import { recordGlobalEngagement } from "@/lib/globalPopularity";

const headline = "One system. End-to-end lighting execution.";
const positioning = "codelumen designs, engineers, and delivers architectural lighting as a single integrated system, replacing the fragmented handoffs between designer, manufacturer, controls vendor, and installer.";
const systemLine = "Design. Products. Controls. Execution. One coordinated system.";

const SLIDE_DURATION_MS = HERO.intervalMs;
// Cinematic crossfade — long, gentle dissolve. Linear easing keeps the
// combined opacity of the two layers at ~1 across the whole transition,
// avoiding the mid-fade brightness dip that reads as "harsh".
const FADE_S = 4.5;
// Ken Burns drift: imperceptible scale travel across the full visible span
// (fade-in + hold + fade-out). 3% over ~10s reads as breath, not movement.
const KEN_BURNS_FROM = 1.0;
const KEN_BURNS_TO = 1.03;
const KEN_BURNS_DURATION_S = SLIDE_DURATION_MS / 1000 + FADE_S * 2;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const DynamicHero = () => {
  const slides = useHeroSlides();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reducedRef = useRef<boolean>(false);
  const hoverTimerRef = useRef<number | null>(null);
  // Only allow hover-pause AFTER the user has actually moved the mouse.
  // Otherwise a cursor that happens to already be inside the hero on page
  // load fires `mouseenter` immediately, pauses the interval, and the
  // slideshow appears frozen on slide 1.
  const mouseMovedRef = useRef<boolean>(false);
  useEffect(() => {
    const onMove = () => { mouseMovedRef.current = true; };
    window.addEventListener("mousemove", onMove, { once: true, passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  // Track mobile viewport so per-slide `positionMobile` overrides can kick in.
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined"
      ? window.matchMedia?.("(max-width: 767px)").matches ?? false
      : false,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalVariant, setModalVariant] = useState<"consultation" | "demo">("consultation");
  const openModal = useCallback((variant: "consultation" | "demo") => {
    setModalVariant(variant);
    setModalOpen(true);
  }, []);

  // Detect reduced motion once
  useEffect(() => {
    reducedRef.current = prefersReducedMotion();
  }, []);

  // Preserve the currently-visible slug across `slides` reference changes
  // (e.g. when global popularity resolves and reorders the pool). Resetting
  // index to 0 on every slides change was pre-empting the first crossfade
  // and causing the first image to flash away almost immediately.
  const prevSlugRef = useRef<string | null>(null);
  useEffect(() => {
    if (slides.length === 0) {
      prevSlugRef.current = null;
      return;
    }
    const prevSlug = prevSlugRef.current;
    if (prevSlug == null) {
      // First time slides arrive — start at slot 0.
      setIndex(0);
      prevSlugRef.current = slides[0]?.slug ?? null;
      return;
    }
    // Try to keep showing the same slide the user is currently seeing.
    const keepIdx = slides.findIndex((s) => s.slug === prevSlug);
    if (keepIdx >= 0) {
      if (keepIdx !== index) setIndex(keepIdx);
      // No autoTick bump — the interval keeps its full remaining hold.
    } else {
      // Current slide dropped out of the pool; fall back to slot 0.
      setIndex(0);
      prevSlugRef.current = slides[0]?.slug ?? null;
    }
  }, [slides]);

  // Auto-advance crossfade. `autoTick` lets manual nav reset the interval.
  const [autoTick, setAutoTick] = useState(0);
  useEffect(() => {
    if (reducedRef.current || paused || slides.length === 0) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, SLIDE_DURATION_MS);
    return () => window.clearInterval(id);
  }, [paused, slides.length, autoTick]);


  const goNext = useCallback(() => {
    if (slides.length === 0) return;
    setIndex((i) => (i + 1) % slides.length);
    setAutoTick((t) => t + 1);
  }, [slides.length]);
  const goPrev = useCallback(() => {
    if (slides.length === 0) return;
    setIndex((i) => (i - 1 + slides.length) % slides.length);
    setAutoTick((t) => t + 1);
  }, [slides.length]);

  // Touch swipe — left/right to change slides. Vertical drag is ignored so
  // the page can still scroll normally. Threshold kept low so a quick flick
  // reliably advances; we also track manual nav to shorten the crossfade
  // (default 4.5s feels "stuck" when the user taps/swipes).
  const touchRef = useRef<{ x: number; y: number; t: number } | null>(null);
  const lastManualAt = useRef<number>(0);
  const [manualNav, setManualNav] = useState(0);

  // First-visit nav-arrow hint. Shows subtle left/right chevrons on the
  // hero so visitors discover the slideshow is navigable. Dismisses on
  // first use (click, swipe, tick) and persists via localStorage.
  const HINT_KEY = "cl_hero_arrows_hint_seen_v1";
  const [arrowsHint, setArrowsHint] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (window.localStorage.getItem(HINT_KEY) === "1") return;
    } catch { /* noop */ }
    const id = window.setTimeout(() => setArrowsHint(true), 900);
    const auto = window.setTimeout(() => setArrowsHint(false), 9000);
    return () => { window.clearTimeout(id); window.clearTimeout(auto); };
  }, []);
  const dismissHint = useCallback(() => {
    setArrowsHint(false);
    try { window.localStorage.setItem(HINT_KEY, "1"); } catch { /* noop */ }
  }, []);
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const start = touchRef.current;
    if (!start) return;
    const t = e.touches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    // If the gesture is clearly horizontal, prevent the browser from treating
    // it as a vertical scroll/back gesture so the swipe always lands.
    if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy) * 1.2) {
      e.stopPropagation();
    }
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchRef.current;
    touchRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const dt = Date.now() - start.t;
    const isFlick = Math.abs(dx) > 24 && dt < 350 && Math.abs(dx) > Math.abs(dy);
    const isDrag = Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy);
    if (!isFlick && !isDrag) return;
    lastManualAt.current = Date.now();
    setManualNav((n) => n + 1);
    dismissHint();
    if (dx < 0) goNext();
    else goPrev();
  };


  // Logo click → reset to first slide
  useEffect(() => {
    const handleReset = () => setIndex(0);
    window.addEventListener("reset-hero", handleReset);
    return () => window.removeEventListener("reset-hero", handleReset);
  }, []);

  // Remember which slugs were shown so the next session penalizes repeats,
  // and keep prevSlugRef synced so future `slides` reference changes can
  // preserve the currently-visible slide.
  useEffect(() => {
    const c = slides[index];
    if (c) {
      noteShown(c.slug, DIVERSITY.recentShownMemory);
      prevSlugRef.current = c.slug;
    }
  }, [index, slides]);


  const scrollToNext = () => {
    const targetId = document.getElementById("what-we-do") ? "what-we-do" : "projects";
    import("@/lib/smoothScroll").then((m) => m.scrollToHash(targetId));
  };

  // Subtle horizon parallax — image drifts up at ~0.15× scroll speed while
  // the text block stays put. Reinforces continuity into the next section
  // without competing with the Ken Burns drift.
  const sectionRef = useRef<HTMLElement | null>(null);
  const { scrollY } = useScroll();
  const parallaxY = useTransform(scrollY, [0, 600], [0, -90]);
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  const parallaxEnabled = isDesktop && !reducedRef.current;


  const current = slides[index];
  if (!current) {
    return (
      <section
        id="hero"
        className="relative min-h-[100svh] md:h-screen w-full md:snap-start bg-charcoal"
        aria-hidden
      />
    );
  }

  return (
    <section
      ref={sectionRef}
      id="hero"
      data-header-hero="media"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseEnter={() => {
        if (!mouseMovedRef.current) return; // ignore cursor that was already inside on load
        setPaused(true);
        if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = window.setTimeout(() => {
          const c = slides[index];
          if (!c) return;
          recordSignal("hero_hover", { slug: c.slug, sector: c.sector, service: c.service });
          recordGlobalEngagement("hero_hover", { slug: c.slug, sector: c.sector, service: c.service });
        }, HERO.intervalMs > 1500 ? 1500 : 800);
      }}

      onMouseLeave={() => {
        setPaused(false);
        if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
      }}
      style={{ touchAction: "pan-y" }}
      className="relative min-h-[100svh] [@media(max-height:500px)]:min-h-0 [@media(max-height:500px)]:h-[100svh] md:h-screen w-full md:snap-start bg-charcoal overflow-hidden"
    >
      <ConsultationModal open={modalOpen} onOpenChange={setModalOpen} variant={modalVariant} />

      {/* Cinematic crossfade slideshow.
          Each slide is its own absolutely-positioned layer. AnimatePresence
          keeps the outgoing layer mounted while the incoming one fades in,
          so the two opacities sum to ~1 throughout the transition — no
          flash to background, no brightness dip. A very gentle Ken Burns
          drift adds breath without drawing attention. */}
      <motion.div
        style={parallaxEnabled ? { y: parallaxY } : undefined}
        className="absolute -top-24 -bottom-24 left-0 right-0 bg-charcoal will-change-transform"
      >
        <AnimatePresence initial={false}>
          <motion.div
            key={`${current.slug}-${index}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: {
                duration: reducedRef.current ? 0.3 : (Date.now() - lastManualAt.current < 1500) ? 0.6 : FADE_S,
                ease: [0.45, 0, 0.55, 1], // gentle sine in-out, softens the mid-fade brightness bump
              },
            }}
            className="absolute inset-0 will-change-[opacity]"
          >
            <motion.div
              initial={{ scale: reducedRef.current ? 1 : KEN_BURNS_FROM }}
              animate={{ scale: reducedRef.current ? 1 : KEN_BURNS_TO }}
              transition={{
                scale: {
                  duration: KEN_BURNS_DURATION_S,
                  ease: "linear",
                },
              }}
              className="absolute inset-0 will-change-transform"
            >
              <picture>
                {current.srcMobile && (
                  <source media="(max-width: 639px)" srcSet={current.srcMobile} />
                )}
                <img
                  src={current.src}
                  alt={current.alt}
                  width={1920}
                  height={1080}
                  style={{
                    objectPosition:
                      (isMobile && current.positionMobile) ||
                      current.position ||
                      // Mobile default: bias slightly upward so ceiling fixtures,
                      // pendants, and architectural light remain visible when a
                      // landscape photo is cover-cropped into a portrait viewport.
                      "center",
                    objectFit: current.fit ?? "cover",
                  }}
                  className="absolute inset-0 h-full w-full pointer-events-none select-none"
                  decoding="async"
                  loading={index === 0 ? "eager" : "lazy"}
                  // @ts-ignore — fetchpriority is valid HTML
                  fetchpriority={index === 0 ? "high" : "low"}
                  draggable={false}
                />
              </picture>
            </motion.div>
          </motion.div>
        </AnimatePresence>

        {/* Refined editorial overlays — image first, text second.
            These sit ABOVE the slideshow layer and never re-mount, so the
            text wash stays perfectly stable across crossfades (no flicker). */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/10 to-transparent"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 hidden md:block"
          style={{
            background:
              "radial-gradient(70% 75% at 18% 78%, rgba(26,26,26,0.85) 0%, rgba(26,26,26,0.55) 30%, rgba(26,26,26,0.15) 55%, rgba(26,26,26,0) 75%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 md:hidden bg-charcoal/45"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(120% 90% at 50% 50%, transparent 55%, rgba(26,26,26,0.45) 100%)",
          }}
        />
        {/* Solid-charcoal dissolve band so the hero image melts into the next section with no seam. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent to-charcoal"
        />
        {/* Subtle warm codelumen glow, kept off the text area */}
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-32 opacity-[0.14] mix-blend-screen"
          style={{
            background:
              "radial-gradient(45% 40% at 70% 45%, rgba(255,176,102,0.55) 0%, rgba(255,140,60,0.18) 40%, transparent 75%)",
          }}
        />
      </motion.div>

      {/* Rolling preload window: only the next 2 slides. Avoids firing ~58
          requests on mount, which previously delayed LCP and starved the
          rest of the homepage from finishing its initial load. */}
      <div aria-hidden className="hidden">
        {slides.length > 1 &&
          [1, 2].map((offset) => {
            const s = slides[(index + offset) % slides.length];
            if (!s) return null;
            return (
              <img
                key={`preload-${(index + offset) % slides.length}-${s.src}`}
                src={s.src}
                alt=""
                decoding="async"
                // @ts-ignore — fetchpriority is valid HTML
                fetchpriority={offset === 1 ? "high" : "low"}
              />
            );
          })}
      </div>

      {/* Editorial text block — lower-left on desktop, natural flow on mobile so
          the section grows to fit content (no nested scroll trap, no clipping
          under dynamic browser chrome on Samsung/Android). */}
      <div className="relative md:absolute md:inset-0 z-10 flex items-start [@media(max-height:500px)]:items-center md:items-end pt-52 pb-24 [@media(max-height:500px)]:pt-24 [@media(max-height:500px)]:pb-14 md:pt-0 md:pb-32 min-h-[100svh] [@media(max-height:500px)]:min-h-0 [@media(max-height:500px)]:h-full md:min-h-0">
        <div className="w-full px-5 sm:px-6 md:px-12 lg:px-20">
          <div className="max-w-2xl text-center md:text-left mx-auto md:mx-0">
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-sans font-light text-white break-words [text-wrap:balance] [@media(max-height:500px)]:!text-[clamp(1.4rem,4.2vw,2.25rem)]"
              style={{
                fontSize: "clamp(1.9rem, 8vw, 5rem)",
                lineHeight: 1.05,
                letterSpacing: "-0.025em",
              }}
            >
              {headline}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 md:mt-6 [@media(max-height:500px)]:mt-2 text-white/85 font-sans font-light text-base md:text-lg [@media(max-height:500px)]:text-xs tracking-[0.02em] max-w-lg mx-auto md:mx-0 leading-relaxed [@media(max-height:500px)]:line-clamp-2"
            >
              {positioning}
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="mt-4 md:mt-5 text-white/55 font-sans font-light text-[11px] md:text-xs tracking-[0.25em] uppercase [@media(max-height:500px)]:hidden"
            >
              {systemLine}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 md:mt-10 [@media(max-height:500px)]:mt-4 flex flex-col sm:flex-row [@media(max-height:500px)]:flex-row items-stretch sm:items-center [@media(max-height:500px)]:items-center justify-center md:justify-start gap-4 [@media(max-height:500px)]:gap-3 w-full max-w-md sm:max-w-none [@media(max-height:500px)]:max-w-none mx-auto md:mx-0"
            >
              <button
                onClick={() => openModal("consultation")}
                className="group btn-primary-dark btn-on-image whitespace-nowrap w-full sm:w-auto [@media(max-height:500px)]:w-auto sm:min-w-[240px] [@media(max-height:500px)]:min-w-0 [@media(max-height:500px)]:text-xs [@media(max-height:500px)]:px-4 [@media(max-height:500px)]:py-2 justify-center"
              >
                Request a Project Review
                <BtnArrow />
              </button>
              <a
                href="/system"
                className="group btn-primary-dark btn-on-image whitespace-nowrap w-full sm:w-auto [@media(max-height:500px)]:w-auto sm:min-w-[240px] [@media(max-height:500px)]:min-w-0 [@media(max-height:500px)]:text-xs [@media(max-height:500px)]:px-4 [@media(max-height:500px)]:py-2 justify-center"
              >
                Explore the codelumen System
                <BtnArrow />
              </a>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll-down affordance — sits at the very bottom edge, centered,
          BELOW the CTAs and above the progress bar. Tiny so it never competes
          with the headline, CTAs, caption, ticks, or the swipe hint. */}
      <motion.button
        type="button"
        onClick={scrollToNext}
        aria-label="Scroll to next section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className="absolute z-20 left-1/2 -translate-x-1/2 bottom-3 md:bottom-3 inline-flex items-center justify-center text-stone/60 hover:text-stone transition-colors duration-300"
      >
        <motion.span
          animate={reducedRef.current ? undefined : { y: [0, 3, 0] }}
          transition={reducedRef.current ? undefined : { duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="inline-flex"
        >
          <ChevronDown className="w-4 h-4 md:w-5 md:h-5" strokeWidth={1.25} />
        </motion.span>
      </motion.button>

      {/* Subtle nav arrows — mobile-only hint that the hero is swipeable.
          Hidden on desktop; functional buttons that self-dismiss after first
          use or after ~9s. They re-show only if localStorage is cleared. */}
      {slides.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => { dismissHint(); goPrev(); }}
            aria-label="Previous slide"
            className={`group absolute z-20 left-3 top-[38%] -translate-y-1/2 inline-flex items-center justify-center w-10 h-10 rounded-full bg-charcoal/30 text-stone/80 hover:text-stone hover:bg-charcoal/50 transition-all duration-300 md:hidden ${arrowsHint ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            <motion.span
              animate={reducedRef.current || !arrowsHint ? undefined : { x: [0, -3, 0] }}
              transition={reducedRef.current || !arrowsHint ? undefined : { duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="inline-flex"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
            </motion.span>
          </button>
          <button
            type="button"
            onClick={() => { dismissHint(); goNext(); }}
            aria-label="Next slide"
            className={`group absolute z-20 right-3 top-[38%] -translate-y-1/2 inline-flex items-center justify-center w-10 h-10 rounded-full bg-charcoal/30 text-stone/80 hover:text-stone hover:bg-charcoal/50 transition-all duration-300 md:hidden ${arrowsHint ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            <motion.span
              animate={reducedRef.current || !arrowsHint ? undefined : { x: [0, 3, 0] }}
              transition={reducedRef.current || !arrowsHint ? undefined : { duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="inline-flex"
            >
              <ChevronRight className="w-4 h-4 md:w-5 md:h-5" strokeWidth={1.5} />
            </motion.span>
          </button>
        </>
      )}

      {/* Mobile-only first-load swipe hint, centered above the headline */}
      <div className="absolute z-20 top-[14%] md:hidden left-0 right-0 flex justify-center pointer-events-none">
        <SwipeHint storageKey="cl_hero_swipe_hint_seen" label="Swipe to explore projects" />
      </div>


      {/* Progress ticks (lower-right). Project caption removed in Sprint 2 —
          the hero is a positioning statement, not a portfolio reel. */}
      <div className="absolute z-20 bottom-12 md:bottom-12 left-0 right-0 px-6 md:px-12 lg:px-20 flex items-end justify-end gap-2 md:gap-6">


        <div className="pointer-events-auto flex items-center gap-2">
          {slides.slice(0, 8).map((_, i) => (
            <button
              key={i}
              aria-label={`Show slide ${i + 1}`}
              onClick={() => { lastManualAt.current = Date.now(); setManualNav((n) => n + 1); dismissHint(); setIndex(i); setAutoTick((t) => t + 1); }}
              className="group p-2 -m-2"
            >
              <span
                className={`block h-px transition-all duration-500 ${
                  i === index ? "w-8 bg-white/80" : "w-4 bg-white/25 group-hover:bg-white/50"
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DynamicHero;
